import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import QRCodeStyling from 'qr-code-styling'
import { CastService } from '../../../services/cast.service'
import { Subscription } from 'rxjs'
import { GoogleCastQRCodeMode, GoogleCastState } from '../../../interfaces/local-googlecast'

@Component({
  selector: 'app-cast-qrcode',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './cast-qrcode.component.html',
  styleUrls: ['./cast-qrcode.component.scss'],
})
export class CastQRCodeComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription = new Subscription()

  qrcode?: QRCodeStyling

  showSupportMessage = true
  supportMessage!: string

  @ViewChild('canvas') canvas!: ElementRef

  qrcodeMode!: GoogleCastQRCodeMode
  GoogleCastQRCodeMode = GoogleCastQRCodeMode
  gcState?: GoogleCastState

  constructor(
    private castService: CastService,
    private chRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private translateService: TranslateService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.qrcodeMode = this.castService.qrcodeMode
    this.supportMessage = this.translateService.instant('CAST.SUPPORT_MESSAGE')
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.castService.gcState$.subscribe((gcState?: GoogleCastState) => {
        if (JSON.stringify(this.gcState) != JSON.stringify(gcState)) {
          this.gcState = gcState
          this.chRef.detectChanges()
          this.loadQRCode()
        }
      })
    )
  }

  loadQRCode(): void {
    let data = ''
    switch (this.qrcodeMode) {
      case GoogleCastQRCodeMode.CODE: {
        data = this.gcState?.qrcodeUrl ?? ''
        break
      }
      case GoogleCastQRCodeMode.WIFI: {
        const ssid = this.gcState?.wifiSSID
        const password = this.gcState?.wifiPassword
        const authType = 'WPA2'
        data = `WIFI:S:${ssid};T:${authType};P:${password};`
        break
      }
      default: {
        break
      }
    }
    if (this.canvas && this.canvas.nativeElement) {
      this.qrcode = this.generateQRCode(data)
      this.showQRCode()
    }
  }

  private generateQRCode(data: string): QRCodeStyling | undefined {
    if (this.canvas && this.canvas.nativeElement) {
      return new QRCodeStyling({
        width: this.canvas.nativeElement.clientWidth,
        height: this.canvas.nativeElement.clientHeight,
        data: data,
        shape: 'square',
        margin: 10,
        image: './assets/images/googlecast/ic_cast_24dp_white.svg',
        qrOptions: {
          typeNumber: 0,
          mode: 'Byte',
          errorCorrectionLevel: 'H',
        },
        imageOptions: {
          saveAsBlob: true,
          hideBackgroundDots: true,
          imageSize: 0.4,
          margin: 10,
        },
        dotsOptions: {
          type: 'dots',
          color: '#f3f1f1',
          roundSize: true,
          gradient: undefined,
        },
        backgroundOptions: {
          round: 0,
          color: 'transparent',
          gradient: undefined,
        },
        cornersSquareOptions: {
          type: 'dot',
          color: '#f3f1f1',
          gradient: undefined,
        },
        cornersDotOptions: {
          type: 'dot',
          color: '#f3f1f1',
          gradient: {
            type: 'radial',
            rotation: 0,
            colorStops: [
              { offset: 0, color: '#f3f1f1' },
              { offset: 0, color: '#f3f1f1' },
            ],
          },
        },
      })
    } else {
      return undefined
    }
  }

  private showQRCode(): void {
    if (this.canvas && this.canvas.nativeElement) {
      const childElements = this.canvas.nativeElement.childNodes
      for (const child of childElements) {
        this.renderer.removeChild(this.canvas.nativeElement, child)
      }
      this.qrcode?.append(this.canvas.nativeElement)
    }
  }
}
